import Vue from 'vue'
import Vuex from 'vuex'
import seoService from './modules/seoService'
import domainService from './modules/domainService'
import websiteService from './modules/websiteService'
// import siteplusService from './modules/siteplusService'
import languageService from './modules/languageService'
import Amplify from '@aws-amplify/core'
import awsAmplifyConfig from '../awsAmplifyConfig'
import { normalizeSingleAccount } from '../helpers/index'

Vue.use(Vuex)
Amplify.configure(awsAmplifyConfig())

const initialState = () => ({
  // when logged in, this will store the cognito session data of the user
  user: null,
  // this boolean indicates whether a modal should be shown when the user tries
  // to change page, to ask if they are sure because page changes will be lost
  navConfirmation: false,
  // this will hold the account information of whatever the selected account is
  account: null,
  // this will hold a normalised representation of the services belonging to the account
  // with the service as the key, and the id of the service as a value
  services: {},
  // set to true once a user session has been checked for, regardless of its existence or not
  sessionChecked: false,
  // the first element of this array indicates if a user was included as a querystring
  // when the app was directed to. The second element contains that user.
  autoResetPassword: [false, null],
  // logos from the dynamic branding are put in here. At the moment the example bt images
  // are base64 encoded, but if you want to use urls instead, you might want to move the
  // logos into the i18n library along with the other dynamic styles.
  logos: [],
  // Dynamic Brand styles & panels config are stored here
  brandConfig: null,
  // Flag that indicates if config and styles and other data are to be loaded locally or from brandConfig state
  useLocalStyle: true,
  hasNetworkError: false,
  background: null,
  // Used for getting partner config and settings
  hostname: '',
  // Number of accounts the customer has
  accountCount: 0,
  // SSO Redirect Call
  ssoRedirect: null,

  // displayName to show in dashboard is present at customer level
  displayName: null,

  // BT tour slider state value , show logic will be started by dashboard life cicle
  showSliderBT: null,

  // BT show getting started box
  showGettingStarted: false,

  // Open assign popup
  skipBusinessProfile: false,

  // Ecomm tour slider
  showSliderEcomm: null,

  showAssignServices: false
})

export default new Vuex.Store({
  modules: {
    seoService,
    domainService,
    languageService,
    websiteService
  },
  state: {
    ...initialState()
  },
  mutations: {
    setNetworkError (state, status) {
      state.hasNetworkError = status
    },
    setLoggedInStatus (state, status) {
      state.isLoggedIn = status
    },
    pageNavRequiresConfirmation (state, status) {
      state.navConfirmation = status
    },
    setAuthState (state, status) {
      state.authState = status
    },
    setUserDetails (state, userDetails) {
      state.user = userDetails
    },
    setActiveAccount (state, account) {
      state.account = account
    },
    setAvailableServices (state, services = {}) {
      state.services = services
    },
    setSessionCheckedStatus (state, status) {
      state.sessionChecked = status
    },
    resetStore (state) {
      state = { ...state, ...initialState() }
    },
    setAutoPasswordReset (state, tuple) {
      state.autoResetPassword = tuple
    },
    setLogos (state, logos) {
      state.logos = logos
    },
    setBrandConfig (state, data) {
      state.brandConfig = data
    },
    setUseLocalStyle (state, value) {
      state.useLocalStyle = value
    },
    setBackground (state, background) {
      state.background = background
    },
    setHostName (state, hostname) {
      state.hostname = hostname
    },
    setAccountCount (state, count) {
      state.accountCount = count
    },
    setSSORedirect (state, value) {
      state.ssoRedirect = value
    },
    setDisplayName (state, value) {
      state.displayName = value
    },
    setServices (state, value) {
      state.services = {}
    }
  },
  actions: {
    addLogos (ctx, logos) {
      ctx.commit('setLogos', logos)
    },
    addBackground (ctx, background) {
      ctx.commit('setBackground', background)
    },
    async handleSession (ctx) {
      try {
        // If a user session is in localstorage, commit it to the store
        const user = await Amplify.Auth.currentAuthenticatedUser()
        if (user && user.signInUserSession) {
          ctx.commit('setUserDetails', user)
        }
      } catch (e) {
        ctx.commit('setUserDetails', null)
      } finally {
        ctx.commit('setSessionCheckedStatus', true)
      }
    },
    changeAccount (ctx, account) {
      const { entities } = normalizeSingleAccount(account)
      ctx.commit('setAvailableServices', entities.services)
      ctx.commit('setActiveAccount', account)
    },
    setPageNavRequiresConfirmation (ctx, status) {
      ctx.commit('pageNavRequiresConfirmation', status)
    },
    createAutoPasswordResetTuple (ctx, user) {
      ctx.commit('setAutoPasswordReset', [Boolean(user), user])
    },
    setHostName (ctx, hostname) {
      ctx.commit('setHostName', hostname)
    },
    setBrandConfig (ctx, config) {
      ctx.commit('setBrandConfig', config)
    },
    setUseLocalStyle (ctx, value) {
      ctx.commit('setUseLocalStyle', value)
    },
    setAccountCount (ctx, value) {
      ctx.commit('setAccountCount', value)
    },
    setSSORedirect (ctx, value) {
      ctx.commit('setSSORedirect', value)
    },
    setDisplayName (ctx, value) {
      ctx.commit('setDisplayName', value)
    },
    clearSessionData (ctx) {
      ctx.commit('setUserDetails', null)
      ctx.commit('setActiveAccount', null)
      ctx.commit('setSSORedirect', null)
      ctx.commit('setAccountCount', 0)
      ctx.commit('setDisplayName', null)
      ctx.commit('setServices', {})
    }
  },
  getters: {
    username ({ user }) {
      if (user) {
        return (user.attributes && user.attributes.preferred_username) ||
          user.username
      }
    },
    getUserJWT ({ user }) {
      if (user) {
        if (user.signInUserSession && user.signInUserSession.accessToken) {
          return  user.signInUserSession.idToken.jwtToken
        }        
      }
      return null
    },
    displayName (state) {
      return state.displayName
    },
    customerId ({ user }) {
      if (user) {
        return (user.attributes && user.attributes['custom:nts_customer_id'])
      }
    },
    background (state) {
      return state.background
    },
    clientCredentials (state) {
      const allButAfterLastSlash = /^.+\//
      if (state.user && state.user.signInUserSession) {
        const credentialId = state.user.signInUserSession.idToken.payload.sub
        // this is the cognito userpool_id, found in the url in the iss of the payload. It is the last segment of the url
        const credentialProvider = state.user.signInUserSession.idToken.payload.iss.replace(allButAfterLastSlash, '')

        return {
          credentialId,
          credentialProvider
        }
      }
    },
    getUseLocalStyle (state) {
      return state.seLocalStyle
    },
    serviceEnabled: (state) => (section) => {
      if (state.services != null && Object.prototype.hasOwnProperty.call(state.services, section) && state.services.section !== '') {
        return true
      }
      return false
    },
    brandConfigValue: (state) => (section, field) => {
      if (state.brandConfig != null && Object.prototype.hasOwnProperty.call(state.brandConfig, section) && Object.prototype.hasOwnProperty.call(state.brandConfig[section], field)) {
        return state.brandConfig[section][field]
      }
      return null
    },
    brandConfigSection: (state) => (section) => {
      if (state.brandConfig != null && Object.prototype.hasOwnProperty.call(state.brandConfig, section)) {
        return state.brandConfig[section]
      }
      return null
    },
    brandCustomStyles (state) {
      if (state.brandConfig != null) {
        const styles = { colors: {}, logos: [], favicon: {}, background: {} }

        if (Object.prototype.hasOwnProperty.call(state.brandConfig, 'colors')) {
          styles.colors = state.brandConfig.colors
        }
        if (Object.prototype.hasOwnProperty.call(state.brandConfig, 'logos')) {
          styles.logos = state.brandConfig.logos
        }
        if (Object.prototype.hasOwnProperty.call(state.brandConfig, 'favicon')) {
          styles.favicon = state.brandConfig.favicon
        }
        if (Object.prototype.hasOwnProperty.call(state.brandConfig, 'background')) {
          styles.background = state.brandConfig.background
        }

        return styles
      }
      return null
    },
    hostname (state) {
      return state.hostname
    },
    accountCount (state) {
      return state.accountCount
    },
    ssoRedirect (state) {
      return state.ssoRedirect
    },
    user (state) {
      return state.user
    }
  }
})
